import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import {
  DEFAULT_CURRENCY,
  formatCockpitMoney,
  formatCockpitPercent,
  formatCockpitPercentChange,
  getExpectedExitQuarters,
  isPeriodOpen as getIsPeriodOpen,
  getRevenueAndSobActuals,
  getRevenueAndSobExpectedExit,
  getRevenueAndSobQuota,
  LAST_FQUARTER,
  OVERALL_CATEGORY,
} from '../../lib'
import { setCockpitRevenueAndSobView } from '../../model'
import {
  useCockpitBaselineAndPlan,
  useCockpitFinancialPlanQuality,
  useEffectiveActualsSync,
  useFilteredFinSobRows,
} from '../../model/hooks'
import {
  selectCockpitCategory,
  selectCockpitCurrency,
  selectCockpitMetric,
  selectCockpitPeriods,
  selectCockpitShowQuota,
  selectIsCockpitPrevFYearClosing,
} from '../../model/selectors'
import { CockpitCollapsedSummary } from '../components/cockpitCollapsedSummary'

import { CockpitMetric } from './components'

interface ICockpitRevenueAndSobCollapsedProps {
  onExpand?: () => void
}

export const CockpitRevenueAndSobCollapsed = ({
  onExpand,
}: ICockpitRevenueAndSobCollapsedProps) => {
  const dispatch = useAppDispatch()
  const actualsType = useSelector(selectCockpitMetric)
  const currency = useSelector(selectCockpitCurrency)
  const isLocalCurrency = currency !== DEFAULT_CURRENCY
  const periods = useSelector(selectCockpitPeriods)
  const showQuota = useSelector(selectCockpitShowQuota)
  const isPrevFYearClosing = useSelector(selectIsCockpitPrevFYearClosing)
  const selectedCategory = useSelector(selectCockpitCategory)
  const category = selectedCategory ?? OVERALL_CATEGORY
  const { targetRows, actualsRows, quotaRows } = useFilteredFinSobRows()

  const { fYear, currentFQuarter } = useEffectiveActualsSync()

  const isPeriodOpen = useMemo(
    () => getIsPeriodOpen(periods, currentFQuarter),
    [periods, currentFQuarter]
  )

  const { baseline, plan } = useCockpitBaselineAndPlan()

  const actuals = useMemo(
    () =>
      getRevenueAndSobActuals({
        rows: actualsRows,
        selectedFYear: fYear,
        actualsType,
        isLocalCurrency,
        category,
      }),
    [actualsRows, fYear, actualsType, isLocalCurrency, category]
  )

  const expectedExit = useMemo(
    () =>
      !isPeriodOpen
        ? { sales: null, sob: null }
        : getRevenueAndSobExpectedExit({
            rows: [...actualsRows, ...targetRows],
            selectedFYear: fYear,
            currentFQuarter,
            actualsType,
            isLocalCurrency,
            category,
          }),
    [
      isPeriodOpen,
      actualsRows,
      targetRows,
      fYear,
      currentFQuarter,
      actualsType,
      isLocalCurrency,
      category,
    ]
  )

  /** Which quarters the expected exit reads as actuals and which as plan — what the Expected Exit
   *  cell's tooltip lists. */
  const expectedExitQuarters = useMemo(
    () => getExpectedExitQuarters(periods, currentFQuarter),
    [periods, currentFQuarter]
  )

  /** While the year before the selected one is still in its last quarter: where that year is
   *  expected to exit — Q1–Q3 as booked, Q4 as planned — and the quarters its ⓘ tooltip lists. */
  const prevFYearExpectedExit = useMemo(
    () =>
      isPrevFYearClosing
        ? {
            figures: getRevenueAndSobExpectedExit({
              rows: [...actualsRows, ...targetRows],
              selectedFYear: fYear - 1,
              currentFQuarter: LAST_FQUARTER,
              actualsType,
              isLocalCurrency,
              category,
            }),
            quarters: getExpectedExitQuarters(periods, LAST_FQUARTER),
          }
        : undefined,
    [
      isPrevFYearClosing,
      actualsRows,
      targetRows,
      fYear,
      periods,
      actualsType,
      isLocalCurrency,
      category,
    ]
  )

  const quota = useMemo(
    () =>
      getRevenueAndSobQuota({
        rows: quotaRows,
        selectedFYear: fYear,
        actualsType,
        isLocalCurrency,
        category,
      }),
    [quotaRows, fYear, actualsType, isLocalCurrency, category]
  )

  const planQuality = useCockpitFinancialPlanQuality()

  const formatMoney = (value: number | null) => formatCockpitMoney(value, currency)

  const handleShowQuality = () => {
    dispatch(setCockpitRevenueAndSobView('quality'))
    onExpand?.()
  }

  return (
    <CockpitCollapsedSummary
      quality={planQuality.overall.score}
      qualityLabel={
        <Translation text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.planQuality.title} span />
      }
      onQualityClick={handleShowQuality}
    >
      <CockpitMetric
        fYear={fYear}
        baseline={baseline}
        plan={plan}
        expectedExit={expectedExit}
        expectedExitQuarters={expectedExitQuarters}
        prevFYearExpectedExit={prevFYearExpectedExit}
        actuals={actuals}
        quota={quota}
        showQuota={showQuota}
        isPeriodOpen={isPeriodOpen}
        formatSales={formatMoney}
        formatSob={formatCockpitPercent}
        formatDelta={formatCockpitPercentChange}
      />
    </CockpitCollapsedSummary>
  )
}
